import { Button, Dropdown, type MenuProps, Tooltip } from 'antd';
import clsx from 'clsx';
import type FileAction from 'interfaces/FileAction';
import React, { type Key, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileMenuActionEnum } from '../../enums/FileMenuActionEnum';
import { FilesTableEnum } from '../../enums/FilesTableEnum';
import { useAuth } from '../../hooks/useAuth';
import { useTags } from '../../hooks/useTags';
import type File from '../../interfaces/File';
import { Icon } from '../Core/Icon';
import { hasFileLimitation } from '../../utils/utils';
import { FileLimitationEnum } from '../../enums/FileLimitationEnum';

interface FilesTableActionsCellProps {
  file: File;
  handleFileMenuAction: (filesIds: number[], action: FileMenuActionEnum) => void;
  handleRowContextMenu: (file: File, e: React.MouseEvent) => void;
  filesActions: FileAction[];
  setSelectedTableKeys: (keys: Key[]) => void;
}

const FilesTableActionsCell: React.FC<FilesTableActionsCellProps> = ({
  file,
  handleFileMenuAction,
  handleRowContextMenu,
  filesActions,
  setSelectedTableKeys,
}) => {
  const { t } = useTranslation();
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
  const { tags } = useTags();

  const { isInFileRoute, authenticatedUser, isAdminOrContributor } = useAuth();

  /**
   * @description Handle menu click.
   */
  const handleMenuClick: MenuProps['onClick'] = (info) => {
    if (!file) return;
    handleFileMenuAction([file.id], info.key as FileMenuActionEnum);
    setIsTagsDropdownOpen(false);
  };

  /**
   * @description Tags action items.
   */
  const tagsActionItems: MenuProps['items'] = useMemo(() => {
    const tagAction = filesActions.find((action) => action.key === FileMenuActionEnum.Tags);

    if (!tagAction || !tagAction.children) {
      return [];
    }

    return tagAction.children.map((child) => ({
      key: child.key,
      label: child.label,
      icon: child.icon,
      disabled: child.disabled,
      danger: child.danger,
    }));
  }, [filesActions]);

  /**
   * @description Check if the share button is visible.
   */
  const isShareVisible = useMemo(() => {
    return !hasFileLimitation(file.fileLimitations, FileLimitationEnum.DenyFileShare);
  }, [file, isInFileRoute, authenticatedUser, isAdminOrContributor]);

  /**
   * @description Check if the tags button is visible.
   */
  const isTagsVisible = useMemo(() => {
    return !isInFileRoute(FilesTableEnum.Trash) && tags.length > 0;
  }, [file, isInFileRoute, tags]);

  /**
   * @description Check if the favorites button is visible.
   */
  const isFavoritesVisible = useMemo(() => {
    return (
      !isInFileRoute(FilesTableEnum.Trash) &&
      !isInFileRoute(FilesTableEnum.Hidden) &&
      !file.isQuickShare
    );
  }, [file, isInFileRoute]);

  return (
    <>
      <div
        className={clsx(
          'actions-cell-content absolute right-0 top-0 z-20 flex h-full flex-row items-center justify-center border-l border-gray-medium bg-[#eeeffa] bg-gradient-to-r from-transparent to-[#eeeffa] px-2',
        )}
      >
        {isShareVisible && (
          <Button
            size="small"
            className="mr-1"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFileMenuAction([file.id], FileMenuActionEnum.Share);
            }}
          >
            {t('common.share')}
          </Button>
        )}
        {isFavoritesVisible && (
          <>
            {file.isFavourite ? (
              <Tooltip title="Remove from favorites">
                <Button
                  type="text"
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleFileMenuAction([file.id], FileMenuActionEnum.RemoveFromFavorites);
                  }}
                >
                  <Icon name="StarOffIcon" className="w-5" />
                </Button>
              </Tooltip>
            ) : (
              <Tooltip title="Add to favorites">
                <Button
                  type="text"
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleFileMenuAction([file.id], FileMenuActionEnum.AddToFavorites);
                  }}
                >
                  <Icon name="StarIcon" className="w-5" />
                </Button>
              </Tooltip>
            )}
          </>
        )}
        {isTagsVisible && (
          <Dropdown
            trigger={['click']}
            open={isTagsDropdownOpen}
            onOpenChange={(open) => {
              if (open) {
                setSelectedTableKeys([file.id]);
              }
              setIsTagsDropdownOpen(open);
            }}
            menu={{
              items: tagsActionItems,
              onClick: handleMenuClick,
            }}
            popupRender={(menuNode) => (
              <div
                onMouseLeave={() => {
                  setIsTagsDropdownOpen(false);
                }}
              >
                {menuNode}
              </div>
            )}
          >
            <Button type="text" size="small">
              <Icon name="BookmarkIcon" className="h-4 w-4" />
            </Button>
          </Dropdown>
        )}
        <Button
          type="text"
          size="small"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleRowContextMenu(file, e);
          }}
        >
          <Icon name="EllipsisVerticalIcon" className="w-5" />
        </Button>
      </div>
    </>
  );
};

export default FilesTableActionsCell;
